/*******************************************************
 * AppContext Provider
 * 
 * Provides global access to table data throughout the application.
 * Handles fetching and caching of data from the AppDB.
 * 
 * Key Features:
 * - Centralized data management
 * - Automatic data fetching with loading states
 * - Type-safe access to tables
 * - Error handling and reporting
 ********************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { type InferSelectModel } from 'drizzle-orm';
import { type APIGetSuccessResponse, type SchemaTableName } from '@/src/scripts/libs/typesLib';
import type * as Schema from "@/src/db/models/MainSchema";
import { useAPI } from '@/src/scripts/APIClient'; // Import the API client
import {
    CONTEXT_REFERENCE_STALE_TIME_MS,
    refreshStaleContextQueries,
} from './utils/contextRefreshPolicy';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS

type ManagedAppTableKeys =
    | 'Ref_AccountReqStatuses'
    | 'Ref_Crafts'
    | 'Ref_Disciplines'
    | 'Ref_Roles'
    | 'Ref_TS_AllocationTypes'
    | 'Ref_TS_Statuses'
    | 'Ref_Shifts'
    | 'Ref_Prog_ActivityTypes'
    | 'Ref_Prog_Ques'
    | 'Ref_Prog_ActivityStatuses'
    | 'SettingsGlobal'
    | 'Ref_Prog_ActivityRejections'
    | 'Contractors';

// Maps table names to their corresponding data types from the schema
type TableTypes = {
    [K in SchemaTableName]: InferSelectModel<typeof Schema[K]>;
};

// Main context type definition
interface AppContextType {
    App: { // Contains data for specifically managed tables
        [K in ManagedAppTableKeys]: APIGetSuccessResponse<TableTypes[K]>
    };
    loading: boolean;                               // Global loading state
    ready: boolean;                                 // True after initial load completes
    error: string | null;                           // Global error state
    refreshSettings: () => Promise<void>;           // Refresh settings whenever Home is entered
    refreshReferencesIfStale: () => Promise<void>;  // Refresh reference data after 30 minutes
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS

// Create the context with undefined default value
const AppContext = createContext<AppContextType | undefined>(undefined);
//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS
// Hook to access the AppContext from any component
export function useApp() {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppContextProvider');
    }
    return context;
}
//#endregion


export function AppContextProvider({ children }: { children: ReactNode }) {
    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC

    const [loading, setLoading] = useState(true);    // Loading state
    const [ready, setReady] = useState(false);       // Initial-load readiness
    const [error, setError] = useState<string | null>(null);  // Error state

    // Individual API instances for each table - must be at top level
    const accountReqStatusesAPI = useAPI({ tableName: 'Ref_AccountReqStatuses', schema: 'AppDB' });
    const craftsAPI = useAPI({ tableName: 'Ref_Crafts', schema: 'AppDB' });
    const disciplinesAPI = useAPI({ tableName: 'Ref_Disciplines', schema: 'AppDB' });
    const rolesAPI = useAPI({ tableName: 'Ref_Roles', schema: 'AppDB' });
    const allocationTypesAPI = useAPI({ tableName: 'Ref_TS_AllocationTypes', schema: 'AppDB' });
    const statusesAPI = useAPI({ tableName: 'Ref_TS_Statuses', schema: 'AppDB' });
    const shiftsAPI = useAPI({ tableName: 'Ref_Shifts', schema: 'AppDB' });
    const activityTypesAPI = useAPI({ tableName: 'Ref_Prog_ActivityTypes', schema: 'AppDB' });
    const quesAPI = useAPI({ tableName: 'Ref_Prog_Ques', schema: 'AppDB' });
    const activityStatusesAPI = useAPI({ tableName: 'Ref_Prog_ActivityStatuses', schema: 'AppDB' });
    const activityRejectionsAPI = useAPI({ tableName: 'Ref_Prog_ActivityRejections', schema: 'AppDB' });
    const settingsGlobalAPI = useAPI({ tableName: 'SettingsGlobal', schema: 'AppDB' });
    const contractorsAPI = useAPI({ tableName: 'Contractors', schema: 'AppDB' });
    // Individual queries for each table
    const referenceQueryOptions = { staleTime: CONTEXT_REFERENCE_STALE_TIME_MS };
    const accountReqStatusesQuery = accountReqStatusesAPI.useGet(referenceQueryOptions);
    const craftsQuery = craftsAPI.useGet(referenceQueryOptions);
    const disciplinesQuery = disciplinesAPI.useGet(referenceQueryOptions);
    const rolesQuery = rolesAPI.useGet(referenceQueryOptions);
    const allocationTypesQuery = allocationTypesAPI.useGet(referenceQueryOptions);
    const statusesQuery = statusesAPI.useGet(referenceQueryOptions);
    const shiftsQuery = shiftsAPI.useGet(referenceQueryOptions);
    const activityTypesQuery = activityTypesAPI.useGet(referenceQueryOptions);
    const quesQuery = quesAPI.useGet(referenceQueryOptions);
    const activityStatusesQuery = activityStatusesAPI.useGet(referenceQueryOptions);
    const activityRejectionsQuery = activityRejectionsAPI.useGet(referenceQueryOptions);
    const settingsGlobalQuery = settingsGlobalAPI.useGet();
    const contractorsQuery = contractorsAPI.useGet({ ...referenceQueryOptions, sort: [{ column: 'CompanyName', order: 'asc' }] });
    // Combine all queries into an array for easier processing


    const refreshSettings = async () => {
        await settingsGlobalQuery.refetch();
    };

    const refreshReferencesIfStale = async () => {
        await refreshStaleContextQueries([
            accountReqStatusesQuery,
            craftsQuery,
            disciplinesQuery,
            rolesQuery,
            allocationTypesQuery,
            statusesQuery,
            shiftsQuery,
            activityTypesQuery,
            quesQuery,
            activityStatusesQuery,
            activityRejectionsQuery,
            contractorsQuery,
        ]);
    };

    // Derive loading and error from query states
    const anyLoading = (
        (accountReqStatusesQuery.isLoading || accountReqStatusesQuery.isFetching) ||
        (craftsQuery.isLoading || craftsQuery.isFetching) ||
        (disciplinesQuery.isLoading || disciplinesQuery.isFetching) ||
        (rolesQuery.isLoading || rolesQuery.isFetching) ||
        (allocationTypesQuery.isLoading || allocationTypesQuery.isFetching) ||
        (statusesQuery.isLoading || statusesQuery.isFetching) ||
        (shiftsQuery.isLoading || shiftsQuery.isFetching) ||
        (activityTypesQuery.isLoading || activityTypesQuery.isFetching) ||
        (quesQuery.isLoading || quesQuery.isFetching) ||
        (activityStatusesQuery.isLoading || activityStatusesQuery.isFetching) ||
        (settingsGlobalQuery.isLoading || settingsGlobalQuery.isFetching) ||
        (activityRejectionsQuery.isLoading || activityRejectionsQuery.isFetching) ||
        (contractorsQuery.isLoading || contractorsQuery.isFetching)
    );

    const firstError = (
        accountReqStatusesQuery.error ||
        craftsQuery.error ||
        disciplinesQuery.error ||
        rolesQuery.error ||
        allocationTypesQuery.error ||
        statusesQuery.error ||
        shiftsQuery.error ||
        activityTypesQuery.error ||
        quesQuery.error ||
        activityStatusesQuery.error ||
        settingsGlobalQuery.error ||
        activityRejectionsQuery.error ||
        contractorsQuery.error
    );

    // Keep loading and error in sync with queries
    useEffect(() => {
        setLoading(anyLoading);
    }, [
        accountReqStatusesQuery.isLoading, accountReqStatusesQuery.isFetching,
        craftsQuery.isLoading, craftsQuery.isFetching,
        disciplinesQuery.isLoading, disciplinesQuery.isFetching,
        rolesQuery.isLoading, rolesQuery.isFetching,
        allocationTypesQuery.isLoading, allocationTypesQuery.isFetching,
        statusesQuery.isLoading, statusesQuery.isFetching,
        shiftsQuery.isLoading, shiftsQuery.isFetching,
        activityTypesQuery.isLoading, activityTypesQuery.isFetching,
        quesQuery.isLoading, quesQuery.isFetching,
        activityStatusesQuery.isLoading, activityStatusesQuery.isFetching,
        settingsGlobalQuery.isLoading, settingsGlobalQuery.isFetching,
        activityRejectionsQuery.isLoading, activityRejectionsQuery.isFetching,
        contractorsQuery.isLoading, contractorsQuery.isFetching,
        anyLoading
    ]);

    useEffect(() => {
        if (firstError) {
            setError(firstError instanceof Error ? firstError.message : 'An error occurred');
        } else {
            setError(null);
        }
    }, [firstError]);

    // Mark ready once initial load has finished (do not unset on refetch)
    useEffect(() => {
        if (!anyLoading && !ready) {
            setReady(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyLoading]);

    //#endregion

    //########## MARKDOWN ##########
    // #region MARKDOWN
    const defaultApiResponse: APIGetSuccessResponse<never> = { data: [], requestURL: '', page: 0, pageSize: 0, recordsCount: 0 };

    // Provide context values to children components
    return (
        <AppContext.Provider value={{
            App: {
                Ref_Prog_ActivityTypes: activityTypesQuery.data ?? defaultApiResponse,
                Ref_AccountReqStatuses: accountReqStatusesQuery.data ?? defaultApiResponse,
                Ref_Crafts: craftsQuery.data ?? defaultApiResponse,
                Ref_Disciplines: disciplinesQuery.data ?? defaultApiResponse,
                Ref_Roles: rolesQuery.data ?? defaultApiResponse,
                Ref_TS_AllocationTypes: allocationTypesQuery.data ?? defaultApiResponse,
                Ref_TS_Statuses: statusesQuery.data ?? defaultApiResponse,
                Ref_Shifts: shiftsQuery.data ?? defaultApiResponse,
                Ref_Prog_Ques: quesQuery.data ?? defaultApiResponse,
                Ref_Prog_ActivityStatuses: activityStatusesQuery.data ?? defaultApiResponse,
                SettingsGlobal: settingsGlobalQuery.data ?? defaultApiResponse,
                Ref_Prog_ActivityRejections: activityRejectionsQuery.data ?? defaultApiResponse,
                Contractors: contractorsQuery.data ?? defaultApiResponse,
            },
            loading,
            ready,
            error,
            refreshSettings,
            refreshReferencesIfStale,
        }}>
            {children}
        </AppContext.Provider>
    );
    //#endregion
}
